import {useEffect, useState} from "react";
import {useParams} from "react-router";
import {
    getInventoryById,
    updateInventory,
    getInventoryLog,
    updateInventoryAlert,
    updateInventoryHidden, updateInventorySku, updateInventoryCost,
    updateInventoryBook
} from "../../api/Api";
import {CategoryType, InventoryAdjustmentType, UnitType} from "../../api/Constants";
import {PackagingCard} from "./PackagingCard";
import {ProductCard} from "./ProductCard";
import {ProductInfoCard} from "./ProductInfoCard";
import {Card, Table, Row, Col} from 'antd';
import {QuantityAdjust} from "./QuantityAdjust";
import {toUtcDateString} from "../../util/timeutils";
import {AlertWidget} from "./AlertWidget";
import {InventoryHideWidget} from "./InventoryHideWidget";
import {WholesaleCard} from "./WholesaleCard";
import {BookSelectWidget} from "./BookSelectWidget";
import {displayToFixedUnit, fixedToDisplayUnit} from "../../util/unitutils";

export const InventoryView = ({refresh, onRefresh, onApiFail, props}) => {
    const params = useParams();
    const inventoryId = params.inventoryId;

    const [item, setItem] = useState(undefined);
    const [logs, setLogs] = useState([]);

    useEffect(() => {
        if (inventoryId === undefined) {
            return;
        }

        getInventoryById(inventoryId).then(r => {
            setItem(r);
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });

        getInventoryLog(inventoryId).then(r => {
            const result = r.map(line => {
                return {
                    ...line,
                    key: line.id
                }
            }).sort((a, b) => {
                return new Date(b.timestamp) - new Date(a.timestamp)
            });

            setLogs(result);
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });

    }, [inventoryId, refresh, onApiFail]);

    const onAdjustQuantity = (quantity, mode) => {
        const fixedQuantity = displayToFixedUnit(quantity, item.unit);
        updateInventory(item.id, fixedQuantity, mode===InventoryAdjustmentType.Set).then(r => {
            if (onRefresh) onRefresh();
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        })
    }

    const onAlertChange = (quantity, enable) => {
        updateInventoryAlert(item.id, quantity, enable).then(r => {
            if (onRefresh) onRefresh();
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }

    const onHide = () => {
        updateInventoryHidden(item.id, !item.hidden).then(r => {
            if (onRefresh) onRefresh();
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }

    const onSkuChange = (newSku) => {
        updateInventorySku(item.id, newSku).then(r => {
            if (onRefresh) onRefresh();
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }

    const onCostChange = (costInfo) => {
        updateInventoryCost(item.id, costInfo).then(r => {
            if (onRefresh) onRefresh();
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }

    const onBookChange = (val) => {
        updateInventoryBook(item.id, val).then(r => {
            if (onRefresh) onRefresh();
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }

    const columns = [
        {
            title: 'User',
            dataIndex: 'username',
            key: 'username'
        },
        {
            title: 'Timestamp',
            dataIndex: 'timestamp',
            key: 'timestamp',
            render: (val) => {
                return toUtcDateString(new Date(val));
            },
            sorter: (a, b) => {
                return new Date(a.timestamp) - new Date(b.timestamp)
            }
        },
        {
            'title': 'Action',
            'dataIndex': 'action',
            key: 'action'
        },
        {
            title: 'Value',
            dataIndex: 'value',
            key: 'value',
            render: (val, record, index) => {
                const displayQuantity = fixedToDisplayUnit(val, item.unit);
                return displayQuantity;
            }
        },
    ]

    if (item === undefined) {
        return <div />
    }
    let card;
    if (item.category === CategoryType.Supplies) {
        card = <PackagingCard onCostChange={onCostChange} item={item} />
    } else if (item.category === CategoryType.Packaged) {
        card = <ProductCard onCostChange={onCostChange} item={item}/>
    } else if (item.category === CategoryType.Wholesale) {
        card = <WholesaleCard onCostChange={onCostChange} item={item} />
    }

    const fixedQuantity = fixedToDisplayUnit(item.quantity, item.unit);
    const quantity = (item.category === CategoryType.Wholesale) ? `${fixedQuantity} ${UnitType.Label[item.unit]}` : fixedQuantity

    const labelStyle = {
        fontWeight: 'bold',
    }

    const tablePagination = {
        defaultPageSize: 50,
        showSizeChanger: true,
        pageSizeOptions: [50, 100, 150, 200]
    }

    return <div style={{textAlign: 'left'}}>
        <ProductInfoCard item={item} onSkuChange={onSkuChange} />
        {card}
        <Card title={<div><b>Quantity</b></div>}>
            <Row>
            <Col span={4} style={labelStyle}>Quantity:</Col>
            <Col span={12}>
                <QuantityAdjust onAdjust={onAdjustQuantity}>{quantity}</QuantityAdjust>
            </Col>
            </Row>
            <Row>
                <Col span={4} style={labelStyle}>Book: </Col>
                <Col><BookSelectWidget book={item.book} onBookChange={onBookChange} /></Col>
            </Row>
            <Row>
                <Col span={4} style={labelStyle}>Enable Alert</Col>
                <Col>{item.enableAlert ? "true" : "false"}</Col>
            </Row>
            <Row>
                <Col span={4} style={labelStyle}>Alert Quantity</Col>
                <Col><AlertWidget alertQuantity={item.alertQuantity} enableAlert={item.enableAlert} onChange={onAlertChange}>
                    {item.alertQuantity}
                </AlertWidget></Col>
            </Row>
            <Row>
                <Col span={4} style={{...labelStyle, marginTop: '5px'}}>Hide Inventory:</Col>
                <InventoryHideWidget onHide={onHide} hidden={item.hidden} />
            </Row>
        </Card>
        <Card title={'Logs'}>
            <Table columns={columns} dataSource={logs} pagination={tablePagination} />
        </Card>
    </div>;
}